/**
 * productAccess — the one place that answers "is this product licensed?".
 *
 * WHY THIS IS A THREE-STATE QUESTION, NOT A BOOLEAN
 * ------------------------------------------------
 * `licensed_products` arrives from `/v1/me/dashboard` one round-trip AFTER the
 * shell paints. So at any moment the answer is one of:
 *
 *   resolved + contains    → allow
 *   resolved + absent      → deny
 *   NOT resolved yet       → unknown
 *
 * Every consumer used to collapse `unknown` into `allow`, by seeding state with
 * `['qutime', 'pureoa']` (MainLayout) or by defaulting a parameter to
 * `['qutime']` (useRoleProfile / WorkerLayout). That is fail-OPEN: a PureOA-only
 * tenant saw QuTime face-capture entries — Face templates, Face review, Account →
 * Face matching, the dashboard enrolment banner — flash on first paint, and a
 * direct URL rendered the page outright because the fetch had not landed.
 *
 * Face capture is the sharpest case in the product. QuTime attendance is a phone
 * geofence plus a face match (svdy-rules §16); a PureOA-only org runs approval
 * workflows and captures no biometrics at all. Offering it a face-enrolment
 * screen invites a worker to hand over special-category personal data (GDPR
 * Art. 9) that the org has no lawful basis to process, and the backend will
 * refuse the write anyway — so the only thing the flash can produce is a
 * privacy-relevant prompt followed by an error.
 *
 * `unknown` therefore resolves to DENY here, and the boundaries in
 * ProductBoundary / WorkerProductBoundary render a spinner rather than the page
 * while it is unknown, so the deny is invisible to a licensed tenant instead of
 * being a flash in the other direction.
 *
 * Kept free of React and of any api import so it can be unit-tested directly —
 * the same reason svdy-mobile keeps its `auth_time` reader dependency-free.
 *
 * NOT an authorization check. The backend license middleware is the gate
 * (`/v1/attendance/*` and the face endpoints 403 without `qutime`); this decides
 * what the UI OFFERS.
 */

/** Product codes as they appear in `licensed_products`. A bundle plan carries BOTH. */
export type ProductCode = 'qutime' | 'pureoa';

export interface ProductLicense {
  /** Exactly what the server said. Never pre-seeded with an optimistic guess. */
  products: string[];
  /** False until a response has actually been read. */
  resolved: boolean;
}

/** The honest starting value: nothing known yet. */
export const UNRESOLVED_LICENSE: ProductLicense = { products: [], resolved: false };

/**
 * Fail-closed licence test. Unknown, empty, and a failed fetch are all `false`;
 * only an explicit, resolved membership is `true`.
 */
export function hasProduct(
  license: ProductLicense | null | undefined,
  product: ProductCode,
): boolean {
  if (!license || license.resolved !== true) return false;
  if (!Array.isArray(license.products)) return false;
  return license.products.includes(product);
}
